import { useState } from "react"

import AnalysisOrb from "@/assets/img/palmistry/analysis-orb.png"
import { Button } from "@/components/ui/button"

import { SIGN_GLYPH, type ZodiacSign } from "../../lib/zodiac"
import { ProcessingCore } from "./processing-core"

interface Props {
  title: string
  checklist: string[]
  birthDate?: string
  onDone: () => void
}

const ZODIAC: {
  sign: ZodiacSign
  name: string
  element: string
  planet: string
  until: [number, number]
}[] = [
  { sign: "capricorn", name: "Capricorn", element: "Earth", planet: "Saturn", until: [1, 19] },
  { sign: "aquarius", name: "Aquarius", element: "Air", planet: "Uranus", until: [2, 18] },
  { sign: "pisces", name: "Pisces", element: "Water", planet: "Neptune", until: [3, 20] },
  { sign: "aries", name: "Aries", element: "Fire", planet: "Mars", until: [4, 19] },
  { sign: "taurus", name: "Taurus", element: "Earth", planet: "Venus", until: [5, 20] },
  { sign: "gemini", name: "Gemini", element: "Air", planet: "Mercury", until: [6, 20] },
  { sign: "cancer", name: "Cancer", element: "Water", planet: "Moon", until: [7, 22] },
  { sign: "leo", name: "Leo", element: "Fire", planet: "Sun", until: [8, 22] },
  { sign: "virgo", name: "Virgo", element: "Earth", planet: "Mercury", until: [9, 22] },
  { sign: "libra", name: "Libra", element: "Air", planet: "Venus", until: [10, 22] },
  { sign: "scorpio", name: "Scorpio", element: "Water", planet: "Pluto", until: [11, 21] },
  { sign: "sagittarius", name: "Sagittarius", element: "Fire", planet: "Jupiter", until: [12, 21] },
]

const ELEMENT_SYMBOL: Record<string, string> = { Fire: "🜂", Earth: "🜃", Air: "🜁", Water: "🜄" }
const PLANET_SYMBOL: Record<string, string> = {
  Sun: "☉",
  Moon: "☽",
  Mercury: "☿",
  Venus: "♀",
  Mars: "♂",
  Jupiter: "♃",
  Saturn: "♄",
  Uranus: "♅",
  Neptune: "♆",
  Pluto: "♇",
}
const MONTHS = "January February March April May June July August September October November December".split(
  " ",
)

function zodiacIndex(birthDate?: string): number {
  if (!birthDate) return 7 // Leo
  const [, m, d] = birthDate.split("-").map(Number)
  if (!m || !d) return 7
  const index = ZODIAC.findIndex(({ until }) => m < until[0] || (m === until[0] && d <= until[1]))
  return index === -1 ? 0 : index
}

function formatBornDate(birthDate?: string): string | null {
  if (!birthDate) return null
  const [y, m, d] = birthDate.split("-").map(Number)
  if (!y || !m || !d) return null
  return `Born on ${MONTHS[m - 1]} ${d}, ${y}`
}

/**
 * Palm/astrology analysis screen: the shared processing animation, then the
 * zodiac reveal (sun sign on the orb, birth date, element / moon sign / ruling
 * planet). Matches the Figma "Dark Mode" Analysis frames (Quiz_analysis_1…4).
 */
export function AnalysisStep({ title, checklist, birthDate, onDone }: Props) {
  const [revealed, setRevealed] = useState(false)

  const sunIndex = zodiacIndex(birthDate)
  const sun = ZODIAC[sunIndex]
  const moon = ZODIAC[(sunIndex + 6) % 12]

  if (!revealed) {
    return (
      <ProcessingCore
        orb={AnalysisOrb}
        title={title}
        checklist={checklist}
        onComplete={() => setRevealed(true)}
      />
    )
  }

  return (
    <div className="relative flex flex-1 flex-col px-4">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px]"
        style={{
          background:
            "radial-gradient(120% 90% at 50% -10%, rgba(100,30,220,0.2), transparent 62%)",
        }}
      />

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center gap-5 pb-4 text-center">
        <h1 className="font-display text-2xl font-medium text-purple-light">
          Your First Personal Insights Are Ready
        </h1>

        <div className="relative size-[220px]">
          <img src={AnalysisOrb} alt="" className="absolute inset-0 size-full object-contain" />
          <img
            src={SIGN_GLYPH[sun.sign]}
            alt=""
            className="absolute top-1/2 left-1/2 size-[112px] -translate-x-1/2 -translate-y-1/2"
          />
        </div>

        <div className="flex flex-col items-center gap-1">
          {formatBornDate(birthDate) && (
            <p className="text-sm text-purple-light/70">{formatBornDate(birthDate)}</p>
          )}
          <p className="font-display text-2xl font-medium text-purple-light">Sun Sign - {sun.name}</p>
        </div>

        <div className="flex w-full items-start">
          <RevealStat label="Element" value={sun.element} symbol={ELEMENT_SYMBOL[sun.element]} />
          <RevealStat label="Moon Sign" value={moon.name} glyph={SIGN_GLYPH[moon.sign]} />
          <RevealStat label="Ruling Planet" value={sun.planet} symbol={PLANET_SYMBOL[sun.planet]} />
        </div>

        <Button variant="gradient" size="xl" className="mt-1 w-full" onClick={onDone}>
          Continue
        </Button>
      </div>
    </div>
  )
}

function RevealStat({
  label,
  value,
  symbol,
  glyph,
}: {
  label: string
  value: string
  symbol?: string
  glyph?: string
}) {
  return (
    <div className="flex flex-1 flex-col items-center gap-1">
      <div className="flex size-[72px] items-center justify-center rounded-full bg-purple-light/5">
        {glyph ? (
          <img src={glyph} alt="" className="size-9" />
        ) : (
          <span className="text-3xl leading-none text-purple-light">{symbol}</span>
        )}
      </div>
      <p className="text-sm text-purple-light/70">{label}</p>
      <p className="font-display text-base font-medium text-purple-light">{value}</p>
    </div>
  )
}
